// Fields you want to strip out before sending to API
const UNWANTED_FIELDS = [
  'EquipmentTotal',
  'GrandTotal',
  'LaborTotal',
  'LaborCost',
  'UnitCost',
  'Customers',
  'Building',
  'EstimateType',
  'TCMCostEstimateStatus',
  'TCMPriceBook',
  'CreatedBy',
  'CreatedDate',
  'UpdatedDate',
  'UpdatedBy',
  'Status'
];

export function removeComputedFields(obj) {
  if (!obj || typeof obj !== 'object') return;

  if (Array.isArray(obj)) {
    obj.forEach(removeComputedFields);
    return;
  }

  // Remove computed fields on this object
  UNWANTED_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(obj, field)) {
      delete obj[field];
    }
  });

  // Recurse into nested objects
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value && typeof value === 'object') {
      removeComputedFields(value);
    }
  });
}
/**
 * Project Equipment Total
 * = sum over all estimates of:
 *     sum(UnitPrice * Quantity) for each detail
 *
 * @param {Array<any>} estimates - data.TCMCostEstimates
 * @returns {number}
 */
export function computeProjectEquipmentTotal(estimates) {
  if (!Array.isArray(estimates) || estimates.length === 0) {
    return 0;
  }

  return estimates.reduce((projectSum, estimate) => {
    const details = Array.isArray(estimate.TCMCostEstimateDetails)
      ? estimate.TCMCostEstimateDetails
      : [];

    const estimateEquipmentTotal = details.reduce((sum, detail) => {
      const unitPrice = Number(detail.UnitPrice) || 0;
      const quantity = Number(detail.Quantity) || 0;
      return sum + unitPrice * quantity;
    }, 0);

    return projectSum + estimateEquipmentTotal;
  }, 0);
}

/**
 * Project Labor Total
 * = sum over all estimates of:
 *     sum(LaborPrice * LaborHours) for each detail
 *
 * @param {Array<any>} estimates - data.TCMCostEstimates
 * @returns {number}
 */
export function computeProjectLaborTotal(estimates) {
  if (!Array.isArray(estimates) || estimates.length === 0) {
    return 0;
  }

  return estimates.reduce((projectSum, estimate) => {
    const details = Array.isArray(estimate.TCMCostEstimateDetails)
      ? estimate.TCMCostEstimateDetails
      : [];

    const estimateLaborTotal = details.reduce((sum, detail) => {
      const laborPrice = Number(detail.LaborPrice) || 0;
      const laborHours = Number(detail.LaborHours) || 0;
      return sum + laborPrice * laborHours;
    }, 0);

    return projectSum + estimateLaborTotal;
  }, 0);
}

/**
 * Project Total Cost
 *
 * For each estimate:
 *   equipmentTotal = sum(UnitPrice * Quantity) for details
 *   laborTotal     = sum(LaborPrice * LaborHours) for details
 *   estimateTotal  = (equipmentTotal + laborTotal) * (1 + OMRate)
 *
 * Project Total Cost = sum(estimateTotal) over all estimates.
 *
 * @param {any} project - the full project object (your `data`)
 * @returns {number}
 */
export function computeProjectGrandTotal(project) {
  const estimates = Array.isArray(project?.TCMCostEstimates)
    ? project.TCMCostEstimates
    : [];

  if (estimates.length === 0) {
    return 0;
  }

  return estimates.reduce((projectSum, estimate) => {
    const details = Array.isArray(estimate.TCMCostEstimateDetails)
      ? estimate.TCMCostEstimateDetails
      : [];

    const omRate = Number(estimate.OMRate) || 0;

    // Equipment Total for this estimate
    const equipmentTotal = details.reduce((sum, detail) => {
      const unitPrice = Number(detail.UnitPrice) || 0;
      const quantity = Number(detail.Quantity) || 0;
      return sum + unitPrice * quantity;
    }, 0);

    // Labor Total for this estimate
    const laborTotal = details.reduce((sum, detail) => {
      const laborPrice = Number(detail.LaborPrice) || 0;
      const laborHours = Number(detail.LaborHours) || 0;
      return sum + laborPrice * laborHours;
    }, 0);

    const baseTotal = equipmentTotal + laborTotal;
    const estimateTotal = baseTotal * (1 + omRate);

    return projectSum + estimateTotal;
  }, 0);
}
